import React, { useState, useEffect } from 'react';
import { Send, Mail, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SendEmailPage = () => {
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [variableValues, setVariableValues] = useState({});
  const [sending, setSending] = useState(false);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');

  useEffect(() => {
    fetchTemplates();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      updatePreview();
    }
  }, [selectedTemplate, variableValues]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/email/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplate(template);
    
    if (template && template.variables) {
      // Initialize variable values
      const initialValues = {};
      template.variables.forEach(varName => {
        initialValues[varName] = '';
      });
      setVariableValues(initialValues);
    } else {
      setVariableValues({});
    }
  };

  const searchCustomers = (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomerResults([]);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = customers.filter(customer => {
      const firstName = (customer.first_name || '').toLowerCase();
      const lastName = (customer.last_name || '').toLowerCase();
      const email = (customer.email || '').toLowerCase();
      
      return firstName.includes(term) || lastName.includes(term) || email.includes(term);
    });
    
    setCustomerResults(filtered.slice(0, 5));
  };

  const selectCustomer = (customer) => {
    setRecipientEmail(customer.email || '');
    setCustomerSearch(`${customer.first_name} ${customer.last_name}`);
    setCustomerResults([]);
    
    // Auto-fill customer-related variables
    if (selectedTemplate) {
      const newValues = { ...variableValues };
      if (selectedTemplate.variables.includes('customer_name')) {
        newValues['customer_name'] = `${customer.first_name} ${customer.last_name}`;
      }
      if (selectedTemplate.variables.includes('customer_email')) {
        newValues['customer_email'] = customer.email || '';
      }
      setVariableValues(newValues);
    }
  };

  const updatePreview = () => {
    if (!selectedTemplate) return;
    
    let subject = selectedTemplate.subject;
    let body = selectedTemplate.body;
    
    // Replace variables with values
    Object.keys(variableValues).forEach(varName => {
      const placeholder = `{{${varName}}}`;
      const value = variableValues[varName] || `[${varName}]`;
      subject = subject.replace(new RegExp(placeholder, 'g'), value);
      body = body.replace(new RegExp(placeholder, 'g'), value);
    });
    
    setPreviewSubject(subject);
    setPreviewBody(body);
  };

  const sendEmail = async () => {
    if (!selectedTemplate || !recipientEmail) {
      toast.error('Bitte wählen Sie ein Template und Empfänger aus');
      return;
    }

    // Check if all variables are filled
    const missingVars = selectedTemplate.variables.filter(v => !variableValues[v] || !variableValues[v].trim());
    if (missingVars.length > 0) {
      toast.error(`Bitte füllen Sie alle Variablen aus: ${missingVars.join(', ')}`);
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          recipient_email: recipientEmail,
          variables: variableValues
        })
      });

      if (response.ok) {
        toast.success('✅ Email erfolgreich gesendet!');
        // Reset form
        setSelectedTemplate(null);
        setRecipientEmail('');
        setCustomerSearch('');
        setVariableValues({});
      } else {
        const error = await response.json();
        toast.error(`❌ Fehler: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Fehler beim Senden der Email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Email senden</h1>
        <p className="text-gray-600">Senden Sie eine personalisierte Email an Ihre Kunden</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Form */}
        <Card>
          <CardHeader>
            <CardTitle>Email-Formular</CardTitle>
            <CardDescription>Wählen Sie Template und Empfänger</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Template Selection */}
            <div className="space-y-2">
              <Label htmlFor="template">Email-Template auswählen *</Label>
              <select
                id="template"
                value={selectedTemplate?.id || ''}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              >
                <option value="">-- Bitte wählen --</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Customer Search */}
            <div className="space-y-2">
              <Label htmlFor="customer_search">Kunde suchen</Label>
              <div className="relative">
                <Input
                  id="customer_search"
                  type="text"
                  placeholder="Name oder Email..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    searchCustomers(e.target.value);
                  }}
                  className="pr-10"
                />
                <User className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                
                {/* Search Results Dropdown */}
                {customerResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {customerResults.map(customer => (
                      <div
                        key={customer.id}
                        onClick={() => selectCustomer(customer)}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      >
                        <p className="font-medium text-sm">
                          {customer.first_name} {customer.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{customer.email}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recipient Email */}
            <div className="space-y-2">
              <Label htmlFor="recipient_email">Empfänger Email *</Label>
              <Input
                id="recipient_email"
                type="email"
                placeholder="kunde@example.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>

            {/* Variable Inputs */}
            {selectedTemplate && selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
              <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
                <Label className="text-sm font-medium text-blue-900">
                  Variablen ausfüllen
                </Label>
                {selectedTemplate.variables.map(varName => (
                  <div key={varName} className="space-y-1">
                    <Label className="text-xs text-blue-800">
                      {varName}
                    </Label>
                    <Input
                      placeholder={`Wert für {{${varName}}}`}
                      value={variableValues[varName] || ''}
                      onChange={(e) => setVariableValues({
                        ...variableValues,
                        [varName]: e.target.value
                      })}
                      className="border-blue-300"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Send Button */}
            <Button
              onClick={sendEmail}
              disabled={sending || !selectedTemplate || !recipientEmail}
              className="w-full"
            >
              <Send className="h-4 w-4 mr-2" />
              {sending ? 'Sende...' : 'Email senden'}
            </Button>
          </CardContent>
        </Card>

        {/* Right: Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Vorschau</CardTitle>
            <CardDescription>So wird die Email aussehen</CardDescription>
          </CardHeader>
          <CardContent>
            {selectedTemplate ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-gray-500">Betreff:</Label>
                  <div className="p-3 bg-gray-50 rounded border border-gray-200 mt-1">
                    <p className="font-medium">{previewSubject}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Nachricht:</Label>
                  <div className="p-4 bg-gray-50 rounded border border-gray-200 mt-1 min-h-[300px]">
                    <pre className="whitespace-pre-wrap text-sm font-sans">{previewBody}</pre>
                  </div>
                </div>

                <div className="p-3 bg-blue-50 rounded text-xs text-blue-700">
                  <strong>Verfügbare Variablen:</strong> {selectedTemplate.variables.join(', ')}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400 py-12">
                <Mail className="h-12 w-12 mx-auto mb-3" />
                <p>Wählen Sie ein Template aus</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SendEmailPage;
